import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Filters from 'works/legacy/lib/filters';
import { omit, uniqBy } from 'lodash';
import { RenderFilteredTable } from './render-filtered-table';
import { Spacing } from 'works/legacy/lib/style-helper';
import { MultipleSelectSearch } from '@common/components';

/**
 * @deprecated - Use MultiSelectTableFilter
 */
export function buildMultiSelectTableFilter({
  selectedKeys,
  setSelectedKeys,
  confirm,
  filters,
}) {
  const options = Array.isArray(filters)
    ? filters.map((filter) => ({
        value: filter.value,
        label: filter.text,
      }))
    : [];

  return (
    <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
      <MultipleSelectSearch
        options={options}
        value={selectedKeys}
        onSearch={(value) => {
          value ? setSelectedKeys(value) : setSelectedKeys([]);
          confirm();
        }}
      />
    </section>
  );
}

function byText(a, b) {
  return `${a.text}`.localeCompare(`${b.text}`);
}

function idAndNameToOption({ id, name }) {
  return { text: name, value: id };
}

function byId({ id }) {
  return id;
}

/**
 * Generate an array of options based on an array of objects.
 * @param {Array} data Array of objects
 * @param {Function} toOption Callback to transform each object into an option.
 * @param {Function} uniqByAttribute Callback to extract what makes an item unique.
 * @param {Function} sortBy Callback to sort the items. You can sort by any attribute present in the option.
 * @returns Array of options. Example: [{ text: 'some name', value: 'some value'}].
 */
export function buildOptionsFromData(
  data,
  toOption = idAndNameToOption,
  uniqByAttribute = byId,
  sortBy = byText
) {
  if (!Array.isArray(data) || data.length === 0) {
    return;
  }

  return uniqBy(data, uniqByAttribute).map(toOption).sort(sortBy);
}

// Deprecated - Use buildOptionsFromData
export function filtersForPaginatedAttr(data, uniqByAttribute, toOption) {
  return buildOptionsFromData(data, toOption, uniqByAttribute);
}

export function FilteredTable({
  loading,
  pagination,
  onChange,
  onFilterChange,
  localStorageFilterKey,
  filters,
  ...props
}) {
  const defaultControlFilters =
    filters ??
    ((localStorageFilterKey && Filters.getFilters(localStorageFilterKey)) ||
      {});

  const [controlFilters, setControlFilters] = useState(defaultControlFilters);
  const [sortingAttrs, setSortingAttrs] = useState({});
  const [pageNumber, setPageNumber] = useState(pagination.current || 1);
  const [pageSize, setPageSize] = useState(
    pagination.pageSize || pagination.defaultPageSize || 10
  );

  useEffect(() => {
    setControlFilters(
      filters ??
        ((localStorageFilterKey && Filters.getFilters(localStorageFilterKey)) ||
          {})
    );
  }, [filters, localStorageFilterKey]);

  const internalPagination = useMemo(
    () => ({
      ...pagination,
      showSizeChanger: true,
      current: pageNumber,
      pageSize,
      onChange: (selectedPageNumber) => {
        setPageNumber(selectedPageNumber);
      },
      onShowSizeChange: (_current, size) => {
        setPageNumber(1);
        setPageSize(size);
      },
    }),
    [pageNumber, pageSize, pagination]
  );

  const defaultPagination = {
    // FIXME: This component should not know anything about staffing request or any other domain context
    staffingRequestId: internalPagination.staffingRequestId,
    current: internalPagination.current || 1,
    pageSize,
  };

  const updateSorter = (sorter) => {
    // Only update sorter if it's not null, meaning that this sorter came from Table
    sorter && setSortingAttrs(sorter);
    // And return the latest non-null sorter
    return sorter || sortingAttrs;
  };

  const onDefaultChange = (pagination, tableFilters, sorter) => {
    setControlFilters(tableFilters);
    onFilterChange?.(tableFilters); // Bubbles up changes to parent
    onChange?.(pagination, tableFilters, updateSorter(sorter)); // Triggers data fetching
  };

  const clearAllFilters = () => {
    onFilterChange({});
    setPageNumber(1);
    onDefaultChange(defaultPagination, {}, null);
  };

  const clearFilter = (column, value) => {
    let remaining = value
      ? controlFilters[column].filter((item) => item !== value)
      : [];

    if (remaining.length === 0) {
      remaining = null;
    }

    setPageNumber(1);
    onDefaultChange(
      defaultPagination,
      { ...controlFilters, [column]: remaining },
      null
    );
  };

  return (
    <RenderFilteredTable
      filters={controlFilters}
      onClearAllFilters={clearAllFilters}
      onClearFilter={clearFilter}
      pagination={internalPagination}
      loading={loading}
      onChange={onDefaultChange}
      {...props}
    />
  );
}

FilteredTable.propTypes = {
  ...omit(RenderFilteredTable.propTypes, [
    'onClearAllFilters',
    'onClearFilter',
  ]),
  onFilterChange: PropTypes.func,
  localStorageFilterKey: PropTypes.string,
  filters: PropTypes.object,
  filtersDataReady: PropTypes.bool,
};

FilteredTable.defaultProps = {
  rowKey: 'key',
};
